import React, { useEffect, useState } from "react";
import api, { formatMoney, formatNumber, ABCD_ORDER, ABCD_COLORS } from "../lib/api";
import { GitCompare } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function Compare() {
  const [studies, setStudies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await api.get("/studies");
    setStudies(data);
  })(); }, []);

  const toggle = (id) => {
    setSelected((s) => s.includes(id) ? s.filter(x => x !== id) : (s.length < 4 ? [...s, id] : s));
  };

  const run = async () => {
    if (selected.length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.post("/studies/compare", { study_ids: selected });
      setComparison(data);
    } finally { setLoading(false); }
  };

  const chartData = comparison ? ABCD_ORDER.map((k) => {
    const obj = { code: k };
    comparison.studies.forEach((s) => {
      obj[s.name] = s.abcd_counts[k] || 0;
    });
    return obj;
  }) : null;

  return (
    <div className="p-8 fade-in" data-testid="compare-page">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Análisis comparativo</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-2">Comparar estudios</h1>
      <p className="text-slate-600 mb-6">Selecciona entre 2 y 4 estudios para comparar.</p>

      <div className="card p-6 mb-6">
        <h3 className="font-display text-lg font-bold mb-3">Estudios disponibles ({selected.length}/4 seleccionados)</h3>
        {studies.length === 0 ? (
          <p className="text-slate-500 text-sm">No tienes estudios aún.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {studies.map((s) => {
              const isSel = selected.includes(s.id);
              return (
                <button key={s.id} onClick={() => toggle(s.id)} data-testid={`select-study-${s.id}`}
                        className={"text-left p-4 rounded-lg border-2 transition " + (isSel ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400")}>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-xs text-slate-500 mt-1 font-mono">
                    {s.period_days}d · {formatNumber(s.total_products)} productos
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatMoney(s.total_sales_value)} ventas</div>
                </button>
              );
            })}
          </div>
        )}
        <button onClick={run} disabled={selected.length < 2 || loading} className="btn btn-primary mt-4" data-testid="run-compare-button">
          <GitCompare size={14} /> {loading ? "Comparando…" : "Comparar seleccionados"}
        </button>
      </div>

      {comparison && (
        <div className="space-y-6 fade-in">
          {/* Eficiencia temporal */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-bold mb-1">Eficiencia: Alta vs Baja rotación</h3>
            <p className="text-xs text-slate-500 mb-4">Evolución del ratio entre clasificaciones de alta rotación (AA+A+BB+B) y baja rotación (CC+C+D).</p>
            <div className="overflow-x-auto">
              <table className="dense">
                <thead>
                  <tr>
                    <th>Métrica</th>
                    {comparison.studies.map((s) => <th key={s.id} className="num">{s.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const HIGH = ["AA","A","BB","B"];
                    const LOW = ["CC","C","D"];
                    const compute = (s, codes, field) => {
                      const total = s.abcd_totals ? Object.values(s.abcd_totals).reduce((a,b)=>a+(b[field]||0),0) : 1;
                      const sum = codes.reduce((a,k) => a + (s.abcd_totals?.[k]?.[field] || 0), 0);
                      return total > 0 ? sum / total : 0;
                    };
                    return (
                      <>
                        <tr>
                          <td className="font-semibold">Alta rot. · % Ventas</td>
                          {comparison.studies.map(s => {
                            const v = compute(s, HIGH, "sales_value");
                            return <td key={s.id} className="num text-emerald-700 font-bold">{(v*100).toFixed(1)}%</td>;
                          })}
                        </tr>
                        <tr>
                          <td className="font-semibold">Alta rot. · % Stock</td>
                          {comparison.studies.map(s => {
                            const v = compute(s, HIGH, "stock_value");
                            return <td key={s.id} className="num">{(v*100).toFixed(1)}%</td>;
                          })}
                        </tr>
                        <tr className="bg-emerald-50">
                          <td className="font-semibold">Δ Alta rot. (Vts − Stk)</td>
                          {comparison.studies.map(s => {
                            const v = compute(s, HIGH, "sales_value") - compute(s, HIGH, "stock_value");
                            return <td key={s.id} className={"num font-bold " + (v >= 0 ? "text-emerald-700" : "text-red-600")}>
                              {v >= 0 ? "+" : ""}{(v*100).toFixed(1)} pp
                            </td>;
                          })}
                        </tr>
                        <tr>
                          <td className="font-semibold">Baja rot. · % Ventas</td>
                          {comparison.studies.map(s => <td key={s.id} className="num">{(compute(s, LOW, "sales_value")*100).toFixed(1)}%</td>)}
                        </tr>
                        <tr>
                          <td className="font-semibold">Baja rot. · % Stock</td>
                          {comparison.studies.map(s => <td key={s.id} className="num text-red-700">{(compute(s, LOW, "stock_value")*100).toFixed(1)}%</td>)}
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="font-display text-lg font-bold mb-3">Resumen comparado</h3>
            <div className="overflow-x-auto">
              <table className="dense">
                <thead>
                  <tr>
                    <th>Métrica</th>
                    {comparison.studies.map((s) => <th key={s.id} className="num">{s.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="font-semibold">Periodo</td>{comparison.studies.map(s => <td key={s.id} className="num font-mono">{s.period_days}d</td>)}</tr>
                  <tr><td className="font-semibold">Valor Stock</td>{comparison.studies.map(s => <td key={s.id} className="num">{formatMoney(s.totals.total_stock_value)}</td>)}</tr>
                  <tr><td className="font-semibold">Ventas</td>{comparison.studies.map(s => <td key={s.id} className="num">{formatMoney(s.totals.total_sales_value)}</td>)}</tr>
                  <tr><td className="font-semibold">Rotación</td>{comparison.studies.map(s => <td key={s.id} className="num">{(s.totals.rotacion_global || 0).toFixed(2)}×</td>)}</tr>
                  <tr><td className="font-semibold">Productos D</td>{comparison.studies.map(s => <td key={s.id} className="num">{s.abcd_counts.D}</td>)}</tr>
                  <tr><td className="font-semibold">€ D Muerto</td>{comparison.studies.map(s => <td key={s.id} className="num text-red-600">{formatMoney(s.stock_d_subdivision.d_muerto.stock_value)}</td>)}</tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="font-display text-lg font-bold mb-3">Productos por clasificación ABCD</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {comparison.studies.map((s, i) => {
                  const colors = ["#0F172A", "#10B981", "#F59E0B", "#EF4444"];
                  return <Bar key={s.id} dataKey={s.name} fill={colors[i]} radius={[4, 4, 0, 0]} />;
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
